import { useState, useEffect, useCallback } from 'react';
import axios, { API, authHeaders, errText } from '../api';
import Layout from './Layout';
import { ui } from '../ui';
import DataTable from '../components/DataTable';

// Предзаполненный пароль (demo12345) означало, что админ, не заметив, создаёт
// куратора с общеизвестным паролем — теперь поле пустое при создании, а сброс
// генерирует случайный пароль и показывает его один раз (см. SECURITY_AUDIT_2026-09-18.md S-19).
function generatePassword() {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = new Uint32Array(12);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export default function Curators() {
  const [items, setItems] = useState([]);
  const [phone, setPhone] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchItems = useCallback(() => {
    axios.get(`${API}/admin/curators`, { headers: authHeaders() }).then(r => setItems(r.data));
  }, []);
  useEffect(() => { fetchItems(); }, [fetchItems]);

  const create = async () => {
    if (!phone || !firstName || !password) return;
    setError('');
    setCreating(true);
    try {
      await axios.post(`${API}/admin/curators`, {
        phone, first_name: firstName, last_name: lastName || null, password,
      }, { headers: authHeaders() });
      setPhone(''); setFirstName(''); setLastName(''); setPassword('');
      fetchItems();
    } catch (e) {
      setError(errText(e, 'Не удалось создать куратора'));
    }
    setCreating(false);
  };

  const toggleActive = async (c) => {
    await axios.patch(`${API}/admin/curators/${c.id}`, { is_active: !c.is_active }, { headers: authHeaders() });
    fetchItems();
  };

  const resetPassword = async (c) => {
    const generated = generatePassword();
    const newPassword = window.prompt(
      `Новый пароль для ${c.first_name} (можно изменить, минимум 6 символов) — запишите, показывается один раз:`,
      generated,
    );
    if (!newPassword) return;
    try {
      await axios.patch(`${API}/admin/curators/${c.id}`, { password: newPassword }, { headers: authHeaders() });
      window.alert(`Пароль обновлён: ${newPassword}`);
    } catch (e) {
      window.alert(errText(e, 'Не удалось обновить пароль'));
    }
  };

  const startEdit = (c) => {
    setError('');
    setEditingId(c.id);
    setEditFirstName(c.first_name);
    setEditLastName(c.last_name || '');
    setEditPhone(c.phone);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id) => {
    if (!editFirstName || !editPhone) return;
    setError('');
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/curators/${id}`, {
        first_name: editFirstName, last_name: editLastName || null, phone: editPhone,
      }, { headers: authHeaders() });
      setEditingId(null);
      fetchItems();
    } catch (e) {
      setError(errText(e, 'Не удалось сохранить изменения'));
    }
    setSaving(false);
  };

  const uploadPhoto = async (c, file) => {
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(`${API}/admin/curators/${c.id}/photo`, form, { headers: authHeaders() });
      fetchItems();
    } catch (e) {
      window.alert(errText(e, 'Не удалось загрузить фото'));
    }
  };

  return (
    <Layout>
      <h1 style={ui.title}>Кураторы</h1>

      <div style={ui.card}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Добавить куратора</div>
        <div style={ui.row}>
          <input style={ui.input} placeholder="Телефон, +7..." value={phone} onChange={e => setPhone(e.target.value)} />
          <input style={ui.input} placeholder="Имя" value={firstName} onChange={e => setFirstName(e.target.value)} />
          <input style={ui.input} placeholder="Фамилия (необязательно)" value={lastName} onChange={e => setLastName(e.target.value)} />
          <input style={ui.input} type="password" placeholder="Пароль (мин. 6 символов)" value={password} onChange={e => setPassword(e.target.value)} />
          <button style={ui.btn} onClick={create} disabled={creating}>{creating ? 'Добавляем…' : 'Добавить'}</button>
        </div>
        {error && <div style={ui.error}>{error}</div>}
      </div>

      <div style={ui.card}>
        <DataTable
          rows={items}
          rowKey={c => c.id}
          emptyText="Кураторов пока нет"
          columns={[
            {
              key: 'photo', label: 'Фото', sortable: false,
              render: c => (
                <label style={{ cursor: 'pointer', display: 'inline-block' }}>
                  {c.photo_url ? (
                    <img src={`${API}${c.photo_url}`} alt="" style={{ width: 36, height: 36, borderRadius: 18, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 36, height: 36, borderRadius: 18, background: '#2a3358', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#9aa3bd' }}>
                      +
                    </div>
                  )}
                  <input
                    type="file" accept="image/jpeg,image/png,image/webp" style={{ display: 'none' }}
                    onChange={e => { const f = e.target.files[0]; e.target.value = ''; if (f) uploadPhoto(c, f); }}
                  />
                </label>
              ),
            },
            {
              key: 'first_name', label: 'Имя',
              render: c => editingId === c.id ? (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={{ ...ui.input, width: 90 }} placeholder="Имя" value={editFirstName} onChange={e => setEditFirstName(e.target.value)} />
                  <input style={{ ...ui.input, width: 90 }} placeholder="Фамилия" value={editLastName} onChange={e => setEditLastName(e.target.value)} />
                </div>
              ) : `${c.first_name} ${c.last_name || ''}`.trim(),
            },
            {
              key: 'phone', label: 'Телефон',
              render: c => editingId === c.id
                ? <input style={{ ...ui.input, width: 130 }} value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                : c.phone,
            },
            { key: 'is_active', label: 'Статус', sortValue: c => (c.is_active ? 1 : 0), render: c => (c.is_active ? 'Активен' : 'Заблокирован') },
            {
              key: 'actions', label: '', sortable: false,
              render: c => editingId === c.id ? (
                <>
                  <button style={ui.btn} onClick={() => saveEdit(c.id)} disabled={saving}>{saving ? 'Сохраняем…' : 'Сохранить'}</button>{' '}
                  <button style={ui.btnSecondary} onClick={cancelEdit} disabled={saving}>Отмена</button>
                </>
              ) : (
                <>
                  <button style={ui.btnSecondary} onClick={() => startEdit(c)}>Изменить</button>{' '}
                  <button style={ui.btnSecondary} onClick={() => toggleActive(c)}>
                    {c.is_active ? 'Заблокировать' : 'Разблокировать'}
                  </button>{' '}
                  <button style={ui.btnSecondary} onClick={() => resetPassword(c)}>Сбросить пароль</button>
                </>
              ),
            },
          ]}
        />
      </div>
    </Layout>
  );
}
